import { useState } from 'react';
import {
  CasRenderArrayAsRows,
  CasRenderDate,
  CasTable,
  CasButton,
} from 'src/compass/shared/components';
import { CasEmployeeSelect } from 'src/compass/application';
import { DateFormat, formatDate, getDateInMoment } from 'src/compass/shared/util/casMoment';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';

import RenderTouchpointFrequency, { useTouchpointCache } from './RenderTouchpointFrequency';
import AssignAllFrequencyDialog from './AssignAllFrequencyDialog';
import { RenderRiskManagerName } from './RenderRiskManagerName';
import customerSearchFilter from './_internal/customerSearchFilter';
import useServiceOwnerFilter from './_internal/useServiceOwnerFilter';

const RMC_FILTER = 'employeeId';

interface Props {
  employeeId: number;
}

export default function UnassignedTouchpointsTable({ employeeId: parmEmployeeId }: Props) {
  const { updateTouchpointListing } = useTouchpointCache();
  const dialog = useCasBoolean();
  const [employeeId, setEmployeeId] = useState<number>(parmEmployeeId);
  const [data, setData] = useState<any>(null);
  const withServiceOwnerFilter = useServiceOwnerFilter(RMC_FILTER);

  // Is Service Owner goes in right after RMC - see useServiceOwnerFilter.
  const table = withServiceOwnerFilter([
    customerSearchFilter,
    {
      type: 'component',
      name: RMC_FILTER,
      label: 'RMC',
      initialValue: parmEmployeeId,
      onChange: (value: number) => setEmployeeId(value),
      component: CasEmployeeSelect,
      componentProps: { params: { meAndMyEmps: true } },
    },
  ]);

  return (
    <>
      <CasTable
        key={table.key}
        entity="Utilities/EntityTouchpoint"
        includeExport={false}
        setData={setData}
        columns={[
          {
            field: 'name',
            render: RenderRiskManagerName,
          },
          {
            field: 'nextTouchpointDate',
            label: 'Next Touchpoint',
            render: (props: ICasRenderCellProps) =>
              props.row?._isPending ? '(updating)' : CasRenderDate(props),
            sx: (row: Record<string, any>) =>
              row.nextTouchpointDate <= formatDate(getDateInMoment(), DateFormat.YMD)
                ? { backgroundColor: '#fde53e' }
                : {},
          },
          {
            field: 'contacts',
            sortable: false,
            render: CasRenderArrayAsRows,
          },
          {
            field: 'touchpointFrequency',
            label: 'Freq',
            render: ({ row, column }: ICasRenderCellProps) => {
              const defaultValue = getCasTableCellValue({ row, column });
              return (
                <RenderTouchpointFrequency
                  defaultValue={defaultValue}
                  id={row.id}
                  onUpdated={(nextTouchpointDate) =>
                    updateTouchpointListing({ id: row.id, nextTouchpointDate})
                  }
                />
              );
            },
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            render: CasRenderDate,
          },
        ]}
        filters={table.filters}
        // inactive: active customers only - the API reads a missing value as All.
        additionalPayload={{ frequency: -1, inactive: false }}
        toolbarButtons={[
          <CasButton onClick={dialog.setTrue} disabled={(data?.totalCount ?? 0) === 0} key="assign-all">
            Assign All
          </CasButton>,
        ]}
      />
      {dialog.value && employeeId && (
        <AssignAllFrequencyDialog
          employeeId={employeeId}
          // Assign All covers the customers the filter shows, not every one the RMC is on.
          isRMCOfRecord={table.isServiceOwner}
          onClose={() => dialog.setFalse()}
        />
      )}
    </>
  );
}
